export const VH_SCALE_FACTOR_VAR = '--vh-scale-factor'

/** The fallback in our own output: `N * 1vh` is exactly `Nvh`, i.e. unscaled, wherever the var is undefined. */
const UNSCALED_FALLBACK = '1vh'

const NUMBER = String.raw`[-+]?(?:\d+(?:\.\d+)?|\.\d+)(?:[eE][+-]?\d+)?`

/**
 * Spells a token so it matches in any case: `url` -> `[uU][rR][lL]`.
 *
 * Used for the two tokens author CSS can legitimately vary — the unit (`100VH`) and `url(` — and
 * NOT via the `i` flag, which would fold the whole pattern including `--vh-scale-factor`. CSS
 * compares custom property names case-sensitively, so folding that one would mistake
 * `var(--VH-SCALE-FACTOR, 1vh)` for our own output.
 */
const anyCase = (token: string) => token.replace(/[a-z]/g, (letter) => `[${letter}${letter.toUpperCase()}]`)

/** `vh`, `dvh`, `svh` or `lvh`, in any case. */
const VH_UNIT = String.raw`[dslDSL]?${anyCase('vh')}`

/**
 * A viewport-height length.
 * - lookbehind: the previous char must not be ident-ish, so `.hero-100vh`, `slide100vh` and `--x-100vh`
 *   are left alone while `:-10vh`, `(1vh`, ` 5vh` and `,2vh` match (the sign is part of the match, so
 *   `margin:-10vh` works).
 * - number: `10` | `10.5` | `.5`, with optional scientific notation.
 * - unit: `vh`, `dvh`, `svh` or `lvh` — in the editor every viewport-height unit is scaled as `vh`.
 * - lookahead: the unit must end the token, so `100vhx` and `.top-100vh-b` are left alone.
 */
const VH_LENGTH = String.raw`(?<![\w-])(${NUMBER})${VH_UNIT}(?![\w-])`

/**
 * Spans that must never be rewritten: quoted strings and `url()`. No comment branch — the input is
 * always a postcss declaration value, and postcss strips comments before we ever see it (including
 * inside `calc()` and in custom properties), so a comment span can never reach this regex.
 */
const PROTECTED_SPAN = String.raw`"(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*'|${anyCase('url')}\(\s*(?:"(?:\\[\s\S]|[^"\\])*"|'(?:\\[\s\S]|[^'\\])*'|[^)]*)\)`

/**
 * Our own output, matched so its `1vh` fallback is not scaled a second time (which would nest the
 * fragment on every pass). Scoped to this exact shape: a `vh` length anywhere else in the value is the
 * author's and still gets scaled, including alongside an unrelated mention of the var.
 *
 * Matched literally, unlike the tokens above: this is the string WE build, always lowercase. Author
 * CSS does not reference our private variable, so a differently-cased spelling of it is not our
 * output — and `var(--VH-SCALE-FACTOR, 1vh)` names a property nobody defines, making its `1vh` a raw
 * length to scale like any other.
 */
const GENERATED_FRAGMENT = String.raw`var\(\s*${VH_SCALE_FACTOR_VAR}\s*,\s*${UNSCALED_FALLBACK}\s*\)`

/**
 * One left-to-right pass. Alternatives are ordered: a protected span wins over everything inside it,
 * our generated fragment wins over the `vh` length it contains, and whatever is left is a length to
 * scale. Only the last alternative captures, so an undefined capture means "copy this verbatim".
 *
 * Units and function names are matched in any case (see `anyCase`), so `100VH` is a length to
 * scale and `URL(10vh.png)` is a span to protect — matching those case-sensitively would both miss
 * real lengths and rewrite inside a real `url()`. `--vh-scale-factor` stays case-sensitive, as CSS
 * compares custom property names.
 */
const SCANNER_REGEX = new RegExp(`${PROTECTED_SPAN}|${GENERATED_FRAGMENT}|${VH_LENGTH}`, 'g')

/**
 * Replaces viewport-height lengths in a single CSS declaration value with the scalable
 * `calc(N * var(--vh-scale-factor, 1vh))` form, so that CSS rendered inside the editor's viewer iframe
 * scales with the iframe rather than with the real viewport.
 *
 * Idempotent: re-running over transformed output returns it unchanged.
 *
 * The input must be a postcss declaration VALUE — on a whole stylesheet this would also rewrite
 * selectors and at-rule preludes such as `@media (min-height: 60vh)`, where `var()` is invalid. Being a
 * postcss `decl.value` also means comments are already gone by the time this runs (see
 * `PROTECTED_SPAN`); a raw string that still contains a comment is not a supported input.
 */
export const transformVhUnitsInCssValue = (value: string): string => {
	if (!/vh/i.test(value)) {
		return value
	}

	return value.replace(SCANNER_REGEX, (match: string, vhValue?: string) =>
		vhValue === undefined ? match : `calc(${vhValue} * var(${VH_SCALE_FACTOR_VAR}, ${UNSCALED_FALLBACK}))`
	)
}
